import { useTranslation } from "react-i18next";
import { useFormik } from "formik";

import {
  Button,
  Label,
  Input,
  MoneyInput,
} from "@/components/ui";

import {
  UpdateProcedureRequestDTO,
  usePermission,
  useToast,
  useUpdateProcedureMutation,
} from "@/hooks";
import { ProceduresValidationSchema } from "@/shared";
import { Trash } from "lucide-react";
import { Procedure } from "@/types";
import { useStockItems } from "@/hooks/queries/inventory/useStockItems";
import { DisplayError } from "@/utils";
import { AlertDialogDelete } from "../../dialogs";
import { ProcedureConsumablesFields } from "./procedure-consumables-fields";

type FormCreateProcedureProps = {
  handleClose: () => void;
  procedure: Procedure;
};
export function FormUpdateProcedure({
  handleClose,
  procedure,
}: FormCreateProcedureProps) {
  const { t } = useTranslation();
  const { toast } = useToast();

  const { checkPermission } = usePermission();

  const { mutateAsync, isLoading } = useUpdateProcedureMutation();
  const { data: stockItems = [], isLoading: isLoadingStockItems } =
    useStockItems();

  const initialValues = {
    id: procedure.id,
    name: procedure.name || "",
    value: procedure.value || "",
    stock_items: (procedure.stock_items ?? []).map((item) => ({
      stock_item_id: item.id,
      quantity: Number(item.pivot.quantity),
    })),
    // time: "",
    // exam: procedure.exam || "",
    // covenant: procedure.covenant || "",
  };

  const { handleSubmit, ...formik } = useFormik({
    initialValues,
    validationSchema: ProceduresValidationSchema(),
    onSubmit: async (values) => {
      handleUpdateUserSubmit(values);
    },
  });

  async function handleUpdateUserSubmit(request: UpdateProcedureRequestDTO) {
    try {
      await mutateAsync(request, {
        onSuccess: () => {
          handleClose();
          formik.resetForm();
          toast({
            title: t("LABEL.success"),
            description: t("MESSAGE.updated successfully"),
          });
        },
        onError: (error) => {
          toast({
            title: t("LABEL.error"),
            description: DisplayError(error, t("MESSAGE.error_message"), t),
            variant: "destructive",
          });
        },
      });
    } catch (error) {
      toast({
        title: t("LABEL.error"),
        description: DisplayError(error, t("MESSAGE.error_message"), t),
        variant: "destructive",
      });
    } finally {
      formik.setSubmitting(false);
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="max-h-[70vh] 2xl:max-h-[90vh] flex flex-col gap-4"
    >
      <div className="h-full space-y-2">
        <div className="grid grid-cols-1 gap-2">
          <div className="space-y-1">
            <Label required>{t("LABEL.name")}</Label>
            <Input
              isInvalid={formik.touched.name && formik.errors.name}
              isValid={formik.touched.name && !formik.errors.name}
              {...formik.getFieldProps("name")}
              messageError={formik.errors.name}
            />
          </div>
          <div className="space-y-1">
            <Label required>{t("LABEL.value")}</Label>
            <MoneyInput
              placeholder={t("LABEL.value")}
              defaultValue={formik.values.value}
              isInvalid={formik.touched.value && formik.errors.value}
              isValid={formik.touched.value && !formik.errors.value}
              {...formik.getFieldProps("value")}
              messageError={formik.errors.value}
            />
          </div>
          <ProcedureConsumablesFields
            items={stockItems}
            value={formik.values.stock_items}
            onChange={(stock_items) =>
              formik.setFieldValue("stock_items", stock_items)
            }
            isLoading={isLoadingStockItems}
          />
          {/* <div className="space-y-1">
            <Label required>
              {t("LABEL.time")}/{t("LABEL.minutes")}
            </Label>
            <Input
              isInvalid={formik.touched.time && formik.errors.time}
              isValid={formik.touched.time && !formik.errors.time}
              {...formik.getFieldProps("time")}
              messageError={formik.errors.time}
            />
          </div> */}
          {/* <div className="space-y-1">
            <Label required>{t("LABEL.exam")}</Label>

            <div className="space-y-1">
              <Select
                {...formik.getFieldProps("exam")}
                onValueChange={(value) => {
                  formik.setFieldTouched("exam", true);
                  formik.setFieldValue("exam", value);
                }}
                defaultValue={initialValues.exam}
              >
                <SelectTrigger
                  isInvalid={formik.touched.exam && formik.errors.exam}
                  isValid={formik.touched.exam && !formik.errors.exam}
                  className="disabled:opacity-100"
                >
                  <SelectValue placeholder="Sim ou não" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="sim">{t("LABEL.yes")}</SelectItem>
                    <SelectItem value="não">{t("LABEL.no")}</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <MessageError
                isInvalid={formik.touched.exam && formik.errors.exam}
                messageError={formik.errors.exam}
              />
            </div>
          </div> */}
        </div>
        {/* <div className="grid grid-cols-1 gap-2">
          <div className="space-y-1">
            <Label required>{t("LABEL.agreement")}</Label>

            <div className="space-y-1">
              <Select
                {...formik.getFieldProps("covenant")}
                onValueChange={(value) => {
                  formik.setFieldTouched("covenant", true);
                  formik.setFieldValue("covenant", value);
                }}
                defaultValue={initialValues.covenant}
              >
                <SelectTrigger
                  isInvalid={formik.touched.covenant && formik.errors.covenant}
                  isValid={formik.touched.covenant && !formik.errors.covenant}
                  className="disabled:opacity-100"
                >
                  <SelectValue placeholder="Sim ou não" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="yes">{t("LABEL.yes")}</SelectItem>
                    <SelectItem value="no">{t("LABEL.no")}</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              <MessageError
                isInvalid={formik.touched.covenant && formik.errors.covenant}
                messageError={formik.errors.covenant}
              />
            </div>
          </div>
        </div> */}
      </div>
      <div className="flex justify-center items-center gap-2">
        <Button className="px-10" isLoading={isLoading} disabled={!checkPermission("procedure", "update procedure") || isLoading}>
          {t("LABEL.save")}
        </Button>
        <AlertDialogDelete
          endpoint={`/procedures/${procedure.id}`}
          queryKey="procedures"
          callBack={handleClose}
        >
          <Button
            type="button"
            className=" gap-2 bg-destructive hover:bg-destructive/80 text-destructive-foreground"
            disabled={!checkPermission("procedure", "delete procedure")}
          >
            <Trash size={14} />
            <span>{t("LABEL.delete")}</span>
          </Button>
        </AlertDialogDelete>
      </div>
    </form>
  );
}
